import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useState} from "react";
import axios from "axios";
import LogRocket from 'logrocket';
import {useNavigate} from "react-router";
import {useSearchParams} from "react-router-dom";
import {Cookies} from "fearless-common-ui";
import {routes} from "../../utils";
import {Group, User} from "fearless-common";
import toast from "react-hot-toast";

export const tokenKey = 'authToken'
export const adminTokenKey = 'adminAuthToken'

interface AuthContextInterface {
    /** The logged-in user */
    user?: null | (User & { groups: Group[] });
    /** The user is still loading the subscription. */
    isLoading: boolean;
    /** Function to save a new login token to the React state and store on disk */
    saveToken: (newToken: undefined | null | string, keepTokenAsAdminToken?: boolean) => void;
    /** Log out the logged-in user */
    logout: () => void;
    /** Reload the currently logged-in user */
    reloadUser: () => void;
    /** Close the emulated user session and continue the admin session */
    reinstateAdminToken: () => void;
}

const AuthContex = createContext<AuthContextInterface>({
    user: null,
    isLoading: false,
    reloadUser: () => {},
    saveToken: () => {},
    logout: () => {},
    reinstateAdminToken: () => {},
})


export function AuthProvider({ children }: PropsWithChildren) {
    const [params] = useSearchParams();
    const [user, setUser] = useState<AuthContextInterface['user']>(null)
    const [isLoading, setLoading] = useState(true)
    const navigate = useNavigate()

    const saveToken = useCallback((newToken: undefined | null | string = undefined, keepCurrentTokenAsAdmin = false) => {
        if (newToken && newToken !== 'undefined' && newToken !== 'null') {
            if (keepCurrentTokenAsAdmin) {
                Cookies.set(adminTokenKey, Cookies.get(tokenKey));
                Cookies.remove('XSRF-TOKEN');
                Cookies.remove('fearless_league_session');
            }
            Cookies.set(tokenKey, newToken);
        } else {
            // Remove all Laravel login data from the Cookies.
            Cookies.remove(tokenKey);
            Cookies.remove(adminTokenKey);
            Cookies.remove('XSRF-TOKEN');
            Cookies.remove('fearless_league_session');
            setUser(null);
        }
    }, [setUser]);

    const reinstateAdminToken = useCallback(() => {
        const adminToken = Cookies.get(adminTokenKey);
        if (adminToken) {
            Cookies.set(tokenKey, Cookies.get(adminTokenKey));
            Cookies.remove(adminTokenKey);
            window.location.reload();
        } else {
            toast.error('No admin login session was found');
        }
    }, []);

    const doToken = (useToken: string | undefined | null) => {
        if (useToken) {
            axios.defaults.headers.common['Authorization'] = 'Bearer '+useToken
            axios.get('/me', {
                data: {},
                baseURL: axios.defaults.baseURL,
                headers: {
                    'Authorization': 'Bearer '+useToken,
                    'ContentType': 'application/json',
                    'Accept': 'application/json',
                },
                withCredentials: false,
            })
                .then(({ data }) => {
                    const newUser = data.data;
                    if (newUser && params.has('validateUserId') && `${newUser?.id}` !== params.get('validateUserId')) {
                        window.location.href = routes.logoutWithRedirect(window.location.pathname);
                    } else {
                        setUser(newUser)
                        if (newUser) {
                            LogRocket.identify(newUser.id, {
                                name: `${newUser?.first_name} ${newUser?.last_name}`,
                                email: newUser?.email,
                            });
                        }
                    }
                })
                .catch((e) => {
                    if ([routes.login, routes.twoFactorChallenge].indexOf(window.location.pathname) === -1) {
                        saveToken()
                        navigate(routes.loginWithRedirect(window.location.pathname));
                    }
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    const loadCurrentUser = useCallback(() => {
        doToken(Cookies.get(tokenKey));
    }, [doToken]);

    useEffect(() => {
        if (Cookies.get(tokenKey)) {
            loadCurrentUser()
        } else {
            setLoading(false)
        }
    }, []);

    const logout = useCallback(() => {
        saveToken(undefined);
    }, [saveToken]);

    return (
        <AuthContex.Provider value={{
            user,
            saveToken,
            isLoading,
            reloadUser: loadCurrentUser,
            logout,
            reinstateAdminToken,
        }}>
            {children}
        </AuthContex.Provider>
    )
}

export default function useAuth() {
    return useContext(AuthContex)
}

